import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const AutomatedTriggers = () => {
  const [isCreatingTrigger, setIsCreatingTrigger] = useState(false);
  const [newTrigger, setNewTrigger] = useState({
    name: '',
    type: 'price_threshold',
    condition: 'below',
    value: '',
    quantity: '',
    enabled: true,
    maxExecutions: '',
    validUntil: ''
  });

  const [triggers, setTriggers] = useState([
    {
      id: 1,
      name: 'Low Price Alert',
      type: 'price_threshold',
      condition: 'below',
      value: 80.00,
      quantity: 5000,
      enabled: true,
      maxExecutions: 3,
      executionsUsed: 1,
      validUntil: '2024-12-31',
      lastTriggered: '2024-09-18',
      status: 'active'
    },
    {
      id: 2,
      name: 'Coverage Shortage',
      type: 'exposure_coverage',
      condition: 'below',
      value: 70,
      quantity: 10000,
      enabled: true,
      maxExecutions: 5,
      executionsUsed: 0,
      validUntil: '2024-11-30',
      lastTriggered: null,
      status: 'active'
    },
    {
      id: 3,
      name: 'High Price Stop',
      type: 'price_threshold',
      condition: 'above',
      value: 90.00,
      quantity: -2500,
      enabled: false,
      maxExecutions: 2,
      executionsUsed: 0,
      validUntil: '2024-10-31',
      lastTriggered: null,
      status: 'disabled'
    },
    {
      id: 4,
      name: 'Monthly Rebalance',
      type: 'time_based',
      condition: 'monthly',
      value: 1,
      quantity: 3000,
      enabled: true,
      maxExecutions: 12,
      executionsUsed: 3,
      validUntil: '2025-01-31',
      lastTriggered: '2024-09-01',
      status: 'active'
    }
  ]);

  const triggerTypes = [
    { value: 'price_threshold', label: 'Price Threshold' },
    { value: 'exposure_coverage', label: 'Exposure Coverage' },
    { value: 'time_based', label: 'Time Based' },
    { value: 'volatility', label: 'Volatility Alert' }
  ];

  const conditionOptions = {
    price_threshold: [
      { value: 'below', label: 'Below' },
      { value: 'above', label: 'Above' }
    ],
    exposure_coverage: [
      { value: 'below', label: 'Below' },
      { value: 'above', label: 'Above' }
    ],
    time_based: [
      { value: 'daily', label: 'Daily' },
      { value: 'weekly', label: 'Weekly' },
      { value: 'monthly', label: 'Monthly' }
    ],
    volatility: [
      { value: 'high', label: 'High Volatility' },
      { value: 'low', label: 'Low Volatility' }
    ]
  };

  const handleCreateTrigger = () => {
    if (!newTrigger?.name || !newTrigger?.value || !newTrigger?.quantity) {
      return;
    }

    const trigger = {
      id: triggers?.length + 1,
      ...newTrigger,
      value: parseFloat(newTrigger?.value),
      quantity: parseInt(newTrigger?.quantity),
      maxExecutions: parseInt(newTrigger?.maxExecutions) || 1,
      executionsUsed: 0,
      lastTriggered: null,
      status: newTrigger?.enabled ? 'active' : 'disabled'
    };

    setTriggers([...triggers, trigger]);
    setNewTrigger({
      name: '',
      type: 'price_threshold',
      condition: 'below',
      value: '',
      quantity: '',
      enabled: true,
      maxExecutions: '',
      validUntil: ''
    });
    setIsCreatingTrigger(false);
  };

  const handleToggleTrigger = (id) => {
    setTriggers(triggers?.map(trigger => 
      trigger?.id === id 
        ? { ...trigger, enabled: !trigger?.enabled, status: trigger?.enabled ? 'disabled' : 'active' }
        : trigger
    ));
  };

  const handleDeleteTrigger = (id) => {
    setTriggers(triggers?.filter(trigger => trigger?.id !== id));
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-success bg-success/10';
      case 'disabled': return 'text-muted-foreground bg-muted';
      case 'triggered': return 'text-warning bg-warning/10';
      case 'expired': return 'text-error bg-error/10';
      default: return 'text-muted-foreground bg-muted';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'price_threshold': return 'DollarSign';
      case 'exposure_coverage': return 'Shield';
      case 'time_based': return 'Clock';
      case 'volatility': return 'TrendingUp';
      default: return 'Zap';
    }
  };

  const formatValue = (trigger) => {
    switch (trigger?.type) {
      case 'price_threshold':
        return `€${trigger?.value?.toFixed(2)}`;
      case 'exposure_coverage':
        return `${trigger?.value}%`;
      case 'time_based':
        return trigger?.condition;
      case 'volatility':
        return trigger?.condition;
      default:
        return trigger?.value;
    }
  };

  return (
    <div className="bg-card rounded-lg border shadow-maritime-sm p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-foreground">Automated Trading Triggers</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Configure automated purchase and sale triggers based on market conditions
          </p>
        </div>
        <Button
          onClick={() => setIsCreatingTrigger(true)}
          className="bg-primary text-primary-foreground"
        >
          <Icon name="Plus" size={16} strokeWidth={2} className="mr-2" />
          New Trigger
        </Button>
      </div>
      {/* Create New Trigger Modal */}
      {isCreatingTrigger && (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-1000 flex items-center justify-center p-4">
          <div className="bg-card border border-border rounded-lg shadow-maritime-lg w-full max-w-md">
            <div className="flex items-center justify-between p-6 border-b border-border">
              <h3 className="text-lg font-semibold text-foreground">Create New Trigger</h3>
              <button
                onClick={() => setIsCreatingTrigger(false)}
                className="p-1 hover:bg-muted rounded"
              >
                <Icon name="X" size={20} strokeWidth={2} />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              <Input
                label="Trigger Name"
                type="text"
                value={newTrigger?.name}
                onChange={(e) => setNewTrigger({...newTrigger, name: e?.target?.value})}
                placeholder="Enter trigger name"
                required
              />

              <Select
                label="Trigger Type"
                options={triggerTypes}
                value={newTrigger?.type}
                onChange={(value) => setNewTrigger({...newTrigger, type: value, condition: conditionOptions?.[value]?.[0]?.value || ''})}
              />

              <Select
                label="Condition"
                options={conditionOptions?.[newTrigger?.type] || []}
                value={newTrigger?.condition}
                onChange={(value) => setNewTrigger({...newTrigger, condition: value})}
              />

              <Input
                label={newTrigger?.type === 'price_threshold' ? 'Price (€)' : 
                       newTrigger?.type === 'exposure_coverage' ? 'Coverage (%)' : 'Value'}
                type="number"
                value={newTrigger?.value}
                onChange={(e) => setNewTrigger({...newTrigger, value: e?.target?.value})}
                placeholder="Enter threshold value"
                required
              />

              <Input
                label="Quantity (EUA)"
                type="number"
                value={newTrigger?.quantity}
                onChange={(e) => setNewTrigger({...newTrigger, quantity: e?.target?.value})}
                placeholder="Enter quantity (negative for sell)"
                required
              />

              <Input
                label="Max Executions"
                type="number"
                value={newTrigger?.maxExecutions}
                onChange={(e) => setNewTrigger({...newTrigger, maxExecutions: e?.target?.value})}
                placeholder="Maximum number of executions"
              />

              <Input
                label="Valid Until"
                type="date"
                value={newTrigger?.validUntil}
                onChange={(e) => setNewTrigger({...newTrigger, validUntil: e?.target?.value})}
              />

              <Checkbox
                label="Enable trigger immediately"
                checked={newTrigger?.enabled}
                onChange={(e) => setNewTrigger({...newTrigger, enabled: e?.target?.checked})}
              />
            </div>

            <div className="flex items-center justify-end space-x-3 p-6 border-t border-border">
              <Button
                variant="outline"
                onClick={() => setIsCreatingTrigger(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleCreateTrigger}
                className="bg-primary text-primary-foreground"
              >
                Create Trigger
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* Triggers List */}
      <div className="space-y-4">
        {triggers?.map((trigger) => (
          <div key={trigger?.id} className="bg-muted/30 rounded-lg p-4 border border-border">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="flex items-center justify-center w-10 h-10 bg-primary/10 rounded-lg">
                  <Icon name={getTypeIcon(trigger?.type)} size={20} strokeWidth={2} className="text-primary" />
                </div>
                
                <div>
                  <div className="flex items-center space-x-3">
                    <h4 className="font-medium text-foreground">{trigger?.name}</h4>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(trigger?.status)}`}>
                      {trigger?.status}
                    </span>
                  </div>
                  <div className="text-sm text-muted-foreground mt-1">
                    {trigger?.condition} {formatValue(trigger)} → 
                    {trigger?.quantity > 0 ? ' Buy ' : ' Sell '}
                    {Math.abs(trigger?.quantity)?.toLocaleString()} EUA
                  </div>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <div className="text-right text-sm">
                  <div className="text-foreground font-medium">
                    {trigger?.executionsUsed}/{trigger?.maxExecutions} used
                  </div>
                  <div className="text-muted-foreground">
                    Valid until {new Date(trigger.validUntil)?.toLocaleDateString()}
                  </div>
                </div>
                
                <div className="flex items-center space-x-1">
                  <button
                    onClick={() => handleToggleTrigger(trigger?.id)}
                    className={`p-2 rounded transition-maritime ${
                      trigger?.enabled 
                        ? 'text-success hover:bg-success/10' :'text-muted-foreground hover:bg-muted'
                    }`}
                  >
                    <Icon name={trigger?.enabled ? 'Play' : 'Pause'} size={16} strokeWidth={2} />
                  </button>
                  
                  <button
                    onClick={() => handleDeleteTrigger(trigger?.id)}
                    className="p-2 rounded text-error hover:bg-error/10 transition-maritime"
                  >
                    <Icon name="Trash2" size={16} strokeWidth={2} />
                  </button>
                </div>
              </div>
            </div>

            {trigger?.lastTriggered && (
              <div className="mt-3 pt-3 border-t border-border text-xs text-muted-foreground">
                Last triggered: {new Date(trigger.lastTriggered)?.toLocaleString()}
              </div>
            )}
          </div>
        ))}
      </div>
      {triggers?.length === 0 && (
        <div className="text-center py-12">
          <Icon name="Zap" size={48} className="text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">No Triggers Configured</h3>
          <p className="text-muted-foreground mb-4">
            Set up automated trading triggers to optimize your EUA procurement strategy
          </p>
          <Button
            onClick={() => setIsCreatingTrigger(true)}
            className="bg-primary text-primary-foreground"
          >
            <Icon name="Plus" size={16} strokeWidth={2} className="mr-2" />
            Create Your First Trigger
          </Button>
        </div>
      )}
    </div>
  );
};

export default AutomatedTriggers;